import { useState } from 'react';
import {
  useRoles,
  useDeleteRole,
  useRolePermissions,
} from '@/hooks/useAdmin';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { SkeletonTable } from '@/components/skeletons/SkeletonLoaders';
import { ErrorState } from '@/components/EmptyState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { RoleModal } from '@/components/modals/RoleModal';
import { useToast } from '@/hooks/useToast';
import { Shield, Lock, Users, Plus, Edit, Trash2 } from 'lucide-react';
import type { RolePermission, RoleSummary } from '@/types';

/**
 * Permission badges for one expanded row.
 *
 * Rendered ONLY while the row is expanded, which is what makes the fetch lazy —
 * the existing `useRolePermissions` hook needs no `enabled` flag. The list
 * route returns `permission_count`, not a permission array, so the names come
 * from `GET /admin/roles/{id}/permissions` (#1455).
 *
 * `permission_count` (rendered by the caller) is computed by the backend from
 * DIRECT grants only — it does not include permissions inherited from a
 * parent role. Requesting `includeInherited: false` here keeps the expanded
 * badge list consistent with the number in the button label; the default
 * (`true`, direct + inherited) is left alone for every other caller.
 */
const RolePermissionBadges = ({ roleId }: { roleId: string }) => {
  const { data, isLoading, error } = useRolePermissions(roleId, false);

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading permissions…</p>;
  }

  if (error) {
    return (
      <p className="text-sm text-destructive">
        Could not load permissions for this role.
      </p>
    );
  }

  const permissions: RolePermission[] = data ?? [];

  if (permissions.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No permissions assigned.
      </p>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {permissions.map((permission) => (
        <Badge key={permission.id} variant="outline" className="font-mono text-xs">
          {permission.name}
        </Badge>
      ))}
    </div>
  );
};

export const RoleManagement = () => {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [expandedRole, setExpandedRole] = useState<string | null>(null);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<RoleSummary | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<RoleSummary | null>(null);

  const { data, isLoading, error, refetch } = useRoles({
    page,
    size: 20,
    search: search || undefined,
  });

  const deleteRole = useDeleteRole();
  const { toast } = useToast();

  const roles = data?.items || [];
  const totalPages = data?.pages || 1;

  // Filter roles based on search
  const filteredRoles = roles;

  const toggleExpand = (roleId: string) => {
    setExpandedRole(expandedRole === roleId ? null : roleId);
  };

  const handleSearch = () => {
    setSearch(searchInput.trim());
    setPage(1);
  };

  const handleCreateRole = () => {
    setSelectedRole(null);
    setRoleModalOpen(true);
  };

  const handleEditRole = (role: RoleSummary) => {
    setSelectedRole(role);
    setRoleModalOpen(true);
  };

  const handleDeleteClick = (role: RoleSummary) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!roleToDelete) return;

    try {
      await deleteRole.mutateAsync(roleToDelete.id);
      toast({
        title: 'Success',
        description: `Role ${roleToDelete.name} has been deleted`,
      });
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
    } catch (err: unknown) {
      const error = err as Error;
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete role',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Role Management</h1>
          <p className="text-muted-foreground">
            Manage roles and their associated permissions
          </p>
        </div>
        <Button onClick={handleCreateRole}>
          <Plus className="h-4 w-4 mr-2" />
          Create Role
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardHeader>
          <CardTitle>Search Roles</CardTitle>
          <CardDescription>
            Search by role name or description
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              placeholder="Search roles..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="flex-1"
            />
            <Button onClick={handleSearch}>Search</Button>
            {search && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearch('');
                  setSearchInput('');
                  setPage(1);
                }}
              >
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Roles Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Roles
          </CardTitle>
          <CardDescription>
            {data?.total || 0} role{(data?.total || 0) !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonTable rows={5} />
          ) : error ? (
            <ErrorState
              message={error instanceof Error ? error.message : 'Failed to load roles'}
              onRetry={() => refetch()}
            />
          ) : filteredRoles.length === 0 ? (
            <div className="text-center text-muted-foreground py-12">
              {search ? 'No roles match your search' : 'No roles found'}
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {filteredRoles.map((role) => {
                  const isSystemRole = role.is_system_role;
                  return (
                    <div
                      key={role.id}
                      className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="flex items-center gap-2">
                              <Shield className="h-5 w-5 text-primary" />
                              <h3 className="text-lg font-semibold">{role.name}</h3>
                            </div>
                            <div className="flex gap-2">
                              {isSystemRole && (
                                <Badge variant="secondary">
                                  <Lock className="h-3 w-3 mr-1" />
                                  System Role
                                </Badge>
                              )}
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground mb-3">
                            {role.description}
                          </p>

                          {/* Permissions */}
                          {role.permission_count > 0 && (
                            <div className="mt-3">
                              <button
                                onClick={() => toggleExpand(role.id)}
                                className="text-sm font-medium text-primary hover:underline flex items-center gap-1"
                              >
                                <Users className="h-4 w-4" />
                                {role.permission_count} Permission
                                {role.permission_count !== 1 ? 's' : ''}
                                <span className="ml-1">
                                  {expandedRole === role.id ? '▼' : '▶'}
                                </span>
                              </button>

                              {expandedRole === role.id && (
                                <div className="mt-3 p-3 bg-muted/30 rounded-md">
                                  <RolePermissionBadges roleId={role.id} />
                                </div>
                              )}
                            </div>
                          )}

                          {/* Parent Role */}
                          {role.parent_role_id && (
                            <div className="mt-2 text-sm text-muted-foreground">
                              Inherits from: <span className="font-medium">{role.parent_role_id}</span>
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 ml-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditRole(role)}
                            disabled={isSystemRole}
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteClick(role)}
                            disabled={isSystemRole || deleteRole.isPending}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            About Roles
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-semibold mb-2">System Roles</h4>
            <p className="text-sm text-muted-foreground">
              System roles are predefined and cannot be modified or deleted. They provide
              baseline access patterns for common user types.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-2">Role Hierarchy</h4>
            <p className="text-sm text-muted-foreground">
              Roles can inherit permissions from parent roles, creating a hierarchy that
              simplifies permission management.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-2">Permission Format</h4>
            <p className="text-sm text-muted-foreground">
              Permissions follow the format <code className="bg-muted px-1 py-0.5 rounded">resource:action</code>.
              The wildcard <code className="bg-muted px-1 py-0.5 rounded">*</code> grants all permissions
              for a resource or action.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Role Modal */}
      <RoleModal
        open={roleModalOpen}
        onOpenChange={setRoleModalOpen}
        role={selectedRole}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Role"
        description={
          roleToDelete
            ? `Are you sure you want to delete the role "${roleToDelete.name}"? This action cannot be undone.`
            : ''
        }
        onConfirm={handleDeleteConfirm}
        confirmText="Delete"
        variant="destructive"
        loading={deleteRole.isPending}
      />
    </div>
  );
};
